import "./styles.scss";

import { Card } from "@amzn/stencil-react-components/card";
import { Chip, ChipBackgroundColor, ChipSize } from "@amzn/stencil-react-components/chip";
import { PopoverController, PopoverPosition } from "@amzn/stencil-react-components/dist/submodules/popover";
import { Col, GridItem, Hr, Row, View } from "@amzn/stencil-react-components/layout";
import { StarRating } from "@amzn/stencil-react-components/star-rating";
import { Text } from "@amzn/stencil-react-components/text";
import { TOOLTIP_POSITION, withTooltip } from "@amzn/stencil-react-components/tooltip";
import { IconInformationFill, IconShow, IconSize, IconStar } from "@amzn/stencil-react-icons";
import IconCheckCircleFill from "@amzn/stencil-react-icons/icons/icon-check-circle-fill";
import IconExternalLink from "@amzn/stencil-react-icons/icons/icon-external-link";
import { TaxonomyIdItem } from "@amzn/taxonomy-lib";
import { Link } from "@amzn/uxm-stencil-react-components/lib/src/stencil/link";
import { trackCustomEvent } from "@amzn/uxm-stencil-react-components/lib/src/uxm/custom-event";
import { getString } from "common/uistringlabels/uiStringUtils";
import { config, PERMISSIONS } from "config";
import { AIForMeTool } from "lib/models/aiforme-models";
import { CatalogicalService } from "lib/services";
import { FeedbackModelRecord, FeedbackStats } from "lib/services/catalogical-models";
import { CSRFService } from "lib/services/csrf-service";
import React, { FC, useRef, useState } from "react";

import InfoIconButton from "../InfoIconButton";
const IconCheckCircleFillWithTooltip = withTooltip()(IconCheckCircleFill);
const IconInformationFillWithTooltip = withTooltip()(IconInformationFill);
import { UserPermissions } from "lib/services";

/**
 * Formats a number to display with K for thousands and M for millions
 * with a maximum of 1 decimal place
 * @param num The number to format
 * @returns Formatted string
 */
const formatClickCount = (num?: number): string => {
  if (num === undefined || num === null) return "0";

  if (num >= 1000000) {
    // Format as millions (M)
    return (Math.floor(num / 100000) / 10).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (num >= 1000) {
    // Format as thousands (K)
    return (Math.floor(num / 100) / 10).toFixed(1).replace(/\.0$/, "") + "K";
  } else {
    // Return as is for numbers less than 1000
    return num.toString();
  }
};

import Button, { ButtonVariant } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { useLists } from "common/context/lists/ListsContext";
import { useProductFeedback } from "hooks/useProductFeedback";
import { ListModel } from "lib/models/list-models";

import categoriesOverride from "../../../configuration/AIForMe/categoriesOverride.json";
import { ListActions } from "../ListActions/ListActions";
import { ProductReviewsFlyout } from "../ProductReviews/ProductReviewsFlyout";
import { StarAverageRating, StarAverageRatingSize } from "../Ratings/StarAverageRating";
import { StarDetailedRating, StarDetailedRatingSize } from "../Ratings/StarDetailedRating";
import { SubmitReviewForm } from "../SubmitReviewForm";

type VoteHandler = (taxId: TaxonomyIdItem, vote: number) => Promise<boolean>;
export interface AIForMeItemProps {
  item: AIForMeTool;
  index: number;
  sortingPriorityTag?: string;
  voteHandler: VoteHandler;
  client: CatalogicalService<AIForMeTool>;
  userVote?: FeedbackModelRecord;
  lists?: ListModel[];
}

type LocalFeedbackStats = FeedbackStats & {
  on: Date;
};

export const AiForMeItem: FC<AIForMeItemProps> = (props: AIForMeItemProps) => {
  const toggleAiForMeReviews = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_AI_FOR_ME_REVIEWS);
  const useNewAiForMeTags = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_AIFORME_TAGS);
  const toggleLists = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_LISTS);
  const taxId = new TaxonomyIdItem(props.item.taxonomyId);
  const linkTitle = `External link to Learn more about ${props.item.title}`;

  const { myLists } = useLists();
  const lists = props.lists ?? myLists;

  const [rating, setRating] = useState(props.userVote?.rating ?? 0);
  const [openWriteReviewFlyout, setOpenWriteReviewFlyout] = useState(false);
  const [openCheckReviewsFlyout, setOpenCheckReviewFlyout] = useState(false);
  const [isSubmittingVote, setIsSubmittingVote] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const {
    itemFeedbackStats,
    isLoading: isLoadingFeedback,
    doPostFeedback,
    setItemFeedbackStats,
  } = useProductFeedback({
    domain: "aiforme",
    defaultValues: {
      itemFeedbackStats: props.item.feedback,
    },
  });
  const csrfService = new CSRFService();

  const dataClassification = props.item.dataClassification ?? "Public";
  const securityReviewed =
    props.item.securityReviewed === "yes" ? "Yes" : props.item.securityReviewed === "no" ? "No" : undefined;

  // This is a temporary measure to change the display name of a category
  // This will require a refactor on Catalogical side and this was required as urgent
  const getOptionDisplayName = (option: string) => (categoriesOverride as { [key: string]: string })[option] ?? option;

  const [isOpen, setIsOpen] = useState(false);
  const target = useRef(null);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen(!isOpen);

  const getTags = () => {
    const tags = [];
    if (props.sortingPriorityTag)
      tags.push(
        <div key="sortingPriorityTag" title="Assessment status" className="chip-category">
          <Chip
            key="sortingPriorityTag"
            data-testid="sortingPriorityTag"
            size={ChipSize.ExtraSmall}
            backgroundColor={ChipBackgroundColor.Green}
          >
            {props.sortingPriorityTag}
          </Chip>
        </div>
      );
    if (dataClassification)
      tags.push(
        <div key="dataClassification" title="Data classification" className="chip-category">
          <Chip
            key="dataClassification"
            data-testid="dataClassification"
            size={ChipSize.ExtraSmall}
            backgroundColor={ChipBackgroundColor.Blue}
          >
            {getString("aiForMe.tags.dataClassification", { dataClassification: dataClassification })}
            <InfoIconButton
              ref={target}
              iconSize={IconSize.ExtraSmall}
              data-testid="dataClassificationIcon"
              onClick={(e) => {
                toggle();
                e.preventDefault();
                e.stopPropagation();
              }}
            />
            <PopoverController target={target} isOpen={isOpen} close={close} position={PopoverPosition.TopCenter}>
              <div style={{ padding: "8px 12px", maxWidth: "300px" }}>
                <Text fontSize="T100">
                  {getString("aiForMe.tags.dataClassificationPopover.prefix")}{" "}
                  <Link
                    href={getString("aiForMe.tags.dataClassificationPopover.linkUrl")}
                    target="_blank"
                    rel="noopener noreferrer"
                    touchpoint="dataclassification-wiki"
                  >
                    {getString("aiForMe.tags.dataClassificationPopover.linkText")}
                  </Link>{" "}
                  {getString("aiForMe.tags.dataClassificationPopover.suffix")}
                </Text>
              </div>
            </PopoverController>
          </Chip>
        </div>
      );

    // TODO: Uncomment section to add "Security reviewed" tag
    // if (securityReviewed)
    //   tags.push(
    //     <div key="securityReviewed" title="Security reviewed status" className="chip-category">
    //       <Chip
    //         key="securityReviewed"
    //         data-testid="securityReviewed"
    //         size={ChipSize.ExtraSmall}
    //         backgroundColor={ChipBackgroundColor.Yellow}
    //       >
    //         {getString("aiForMe.tags.securityReviewed", { securityReviewed: securityReviewed })}
    //       </Chip>
    //     </div>
    //   );

    for (const category of props.item.categories) {
      if (props.sortingPriorityTag?.startsWith(category)) continue;

      tags.push(
        <div key={category} title="Category" className="chip-category">
          <Chip key={category} data-testid={category} size={ChipSize.ExtraSmall}>
            {getOptionDisplayName(category)}
          </Chip>
        </div>
      );
    }

    if (props.item.basePlatform)
      tags.push(
        <div key="basePlatform" title="Base Platform" className="chip-category">
          <Chip
            key="basePlatform"
            data-testid="basePlatform"
            size={ChipSize.ExtraSmall}
            backgroundColor={ChipBackgroundColor.Purple}
          >
            {props.item.basePlatform}
          </Chip>
        </div>
      );

    if (props.item.status === "UPDATED")
      tags.push(
        <div key="status" title="Status" className="chip-category">
          <Chip key="status" data-testid="status" size={ChipSize.ExtraSmall} backgroundColor={ChipBackgroundColor.Red}>
            {getString("aiForMe.tags.updatedUnderReview")}
            <IconInformationFillWithTooltip
              tooltipText={<Text>{getString("aiForMe.tags.UnderReviewTooltip")}</Text>}
              size={IconSize.ExtraSmall}
            />
          </Chip>
        </div>
      );

    for (const [index, itemTag] of props.item.tags.entries()) {
      tags.push(
        <div key={`custom-tag-container-${index}`} title="Tag" className="chip-category">
          <Chip
            key={`custom-tag-${index}`}
            data-testid={`custom-tag-${index}`}
            size={ChipSize.ExtraSmall}
            backgroundColor={ChipBackgroundColor.Yellow}
          >
            {itemTag}
          </Chip>
        </div>
      );
    }

    return tags;
  };

  /* istanbul ignore next */
  const trackUxmCustomEventOnVote = (rating: number) => {
    const trackerId = config.analytics.uxmAppId;
    const event = {
      taxonomyId: props.item.taxonomyId.toString(),
      title: props.item.title,
      rating,
    };

    trackCustomEvent(event, "aiforme-vote", trackerId);
  };

  /* istanbul ignore next */
  const onChangeHandler = (e: number) => {
    trackUxmCustomEventOnVote(e);
    setIsSubmittingVote(true);
    const votePromise = props.voteHandler(taxId, e);
    if (votePromise && typeof votePromise.then === "function") {
      votePromise
        .then((ok: boolean) => {
          if (!ok) {
            setErrorMessage(getString("aiForMe.failedVote"));
          } else {
            setRating(e);
            // Get the latest item data from the server
            props.client
              .getCatalogItemByTaxonomyId(taxId)
              .then((response) => {
                if (response && response.item) {
                  // Update the feedback state with the latest data
                  setItemFeedbackStats(response.item.feedback);
                }
              })
              .catch((error) => {
                console.error("Error fetching updated item data:", error);
              });
          }
          setIsSubmittingVote(false);
        })
        .catch((error) => {
          setErrorMessage(getString("aiForMe.failedVote"));
          setIsSubmittingVote(false);
          console.error("Vote submission error:", error);
        });
    } else {
      setIsSubmittingVote(false);
      setRating(props.userVote?.rating ?? 0);
    }
  };

  // unable to add coverage for now as it is disabled
  const buttonCheckReviews = (
    <Button
      variant={ButtonVariant.Tertiary}
      onClick={() => setOpenCheckReviewFlyout(true)} // set to opposite value
      data-test-id={"check-reviews-button"}
      touchpoint="ai-for-me-check-reviews"
    >
      Check reviews
    </Button>
  );

  const renderViewReviewsFlyout = () => {
    return (
      <ProductReviewsFlyout
        productName={props.item.title}
        titleText={"Check reviews"}
        isOpen={openCheckReviewsFlyout}
        setIsOpen={setOpenCheckReviewFlyout}
        setOpenWriteReviewFlyout={setOpenWriteReviewFlyout}
        feedback={itemFeedbackStats}
        taxonomyId={taxId}
        domain="aiforme"
      />
    );
  };

  const renderReviewSubmissionFlyout = () => {
    return (
      <SubmitReviewForm
        productName={props.item.title}
        titleText={"Submit review"}
        isOpen={openWriteReviewFlyout}
        setIsOpen={setOpenWriteReviewFlyout}
        rating={rating}
        setRating={setRating}
        isLoading={isLoadingFeedback}
        doPostFeedback={doPostFeedback}
        setItemFeedback={setItemFeedbackStats}
        taxonomyId={taxId}
        domain="aiforme"
      />
    );
  };

  const renderRatings = () => {
    const popoverContent = (
      <StarDetailedRating
        size={StarDetailedRatingSize.Small}
        averageRating={itemFeedbackStats?.averageRating}
        voteNumber={itemFeedbackStats?.numberOfRatings}
        ratingCounts={itemFeedbackStats?.ratingCounts}
        button={buttonCheckReviews}
      />
    );

    return (
      <Row>
        <StarAverageRating
          size={StarAverageRatingSize.Small}
          averageRating={itemFeedbackStats?.averageRating}
          voteNumber={itemFeedbackStats?.numberOfRatings}
          popoverContent={toggleAiForMeReviews ? popoverContent : undefined}
        />
        <Text fontSize="T100" className="click-count" style={{ marginLeft: "16px" }}>
          <Row columnGap={2} alignItems="center">
            <IconShow size={IconSize.Small} />
            {formatClickCount(props.item.catalogicalGenerated?.interactions?.clicks)} views
          </Row>
        </Text>
      </Row>
    );
  };

  const trackClickInteraction = () => {
    // Track the click interaction
    try {
      csrfService
        .getToken()
        .then((token) => {
          props.client.postInteraction(taxId, token).catch((error) => {
            // Log error but don't disrupt user experience
            console.error("Failed to track interaction:", error);
          });
        })
        .catch((error) => {
          // Log error but don't disrupt user experience
          console.error("Failed to get CSRF token:", error);
        });
    } catch (error) {
      // Log error but don't disrupt user experience
      console.error("Error tracking interaction:", error);
    }
  };

  const renderFooter = () => {
    if (toggleAiForMeReviews) {
      return (
        <Col style={{ marginTop: "16px" }}>
          {isSubmittingVote && <Text fontSize="smaller">{getString("aiForMe.submitting")}</Text>}
          {errorMessage && <Text fontSize="smaller">{errorMessage}</Text>}
          {!isSubmittingVote && !errorMessage && (
            <Row alignItems="center" gridGap={5}>
              <Text fontSize="smaller">{getString("aiForMe.quickRate")}</Text>
              <StarRating name="star-rating" data-testid="star-rating" value={rating} onChange={onChangeHandler} />
            </Row>
          )}
          <Row
            alignItems="center"
            justifyContent="space-between"
            gridGap={32}
            padding={0}
            style={{ marginTop: "16px" }}
          >
            <Button
              variant={ButtonVariant.Tertiary}
              data-test-id="ai-item-submit-review"
              onClick={() => setOpenWriteReviewFlyout(true)}
              touchpoint="ai-for-me-submit-review"
            >
              Submit review
            </Button>
            <Link
              style={{ marginRight: "8px" }}
              data-testid={`AiForMeItemLearnMore-${props.item.title}`}
              href={props.item.learnMoreLink}
              target="_blank"
              title={linkTitle}
              icon={<IconExternalLink title={linkTitle} />}
              touchpoint="learn-more"
              customData={{
                taxonomyId: taxId.toString(),
                title: props.item.title,
              }}
              onClick={() => trackClickInteraction()}
            >
              {getString("aiForMe.learnMore")}
            </Link>
          </Row>
        </Col>
      );
    }

    // old implementation
    return (
      <Row alignItems="center" justifyContent="space-between" gridGap={32} margin={"16px"}>
        <Link
          data-testid={`AiForMeItemLearnMore-${props.item.title}`}
          href={props.item.learnMoreLink}
          target="_blank"
          title={linkTitle}
          icon={<IconExternalLink title={linkTitle} />}
          touchpoint="learn-more"
          customData={{
            taxonomyId: taxId.toString(),
            title: props.item.title,
          }}
          onClick={() => trackClickInteraction()}
        >
          {getString("aiForMe.learnMore")}
        </Link>
        {isSubmittingVote && <Text fontSize="smaller">{getString("aiForMe.submitting")}</Text>}
        {errorMessage && <Text fontSize="smaller">{errorMessage}</Text>}
        {!isSubmittingVote && !errorMessage && (
          <Row alignItems="center" gridGap={5}>
            <Text fontSize="smaller">{getString("aiForMe.rateTool")}</Text>
            <StarRating name="star-rating" data-testid="star-rating" value={rating} onChange={onChangeHandler} />
          </Row>
        )}
      </Row>
    );
  };

  return (
    <GridItem key={taxId.toString()} lg={6} md={4} data-testid="AiForMeItem" data-index={props.index}>
      <Card style={{ minWidth: "100%" }}>
        <Col gridGap={20} style={{ minWidth: "100%", justifyContent: "space-between" }}>
          <>
            <Row alignItems="flex-start" justifyContent="space-between" gridGap={16}>
              <Col>
                <Row alignItems="flex-start" gridGap={5} style={{ flex: 1, overflow: "hidden" }}>
                  <Text
                    data-testid={`AiForMeItemTitle-${props.item.title}`}
                    fontSize="T300"
                    fontWeight={"bold"}
                    style={{ wordBreak: "break-word" }}
                  >
                    {props.item.title}
                  </Text>
                  {props.item.recommended && (
                    <IconCheckCircleFillWithTooltip
                      title="Show tooltip"
                      tooltipText="Recommended"
                      tooltipPosition={TOOLTIP_POSITION.BOTTOM}
                    />
                  )}
                </Row>
                <Row gridGap={8} style={{ flexShrink: 0 }}>
                  {renderRatings()}
                </Row>
              </Col>
              {toggleLists && <ListActions taxonomyId={props.item.taxonomyId} lists={lists} allowedDomain="aiforme" />}
            </Row>
            <Text
              data-testid={`AiForMeItemDescription-${props.item.title}`}
              color="var(--st-dt-color-text-secondary)"
              style={{ overflow: "auto" }}
            >
              {props.item.description}
            </Text>
            <Row gridGap={8} flexWrap="wrap">
              {getTags()}
            </Row>
          </>
          <View style={{ marginTop: "auto" }}>
            <Hr />
            {renderFooter()}
          </View>
        </Col>
      </Card>
      {renderReviewSubmissionFlyout()}
      {openCheckReviewsFlyout && renderViewReviewsFlyout()}
    </GridItem>
  );
};
